import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PlusIcon, PencilIcon, TrashIcon, ChevronDownIcon, ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import { useAuth } from '../auth/AuthContext';
import { Brand, ImsiSeries } from '../types';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import StatusBanner, { Status } from '../components/StatusBanner';

const IMSI_WIDTH = 15;

type SeriesErrors = { imsi_from?: string; imsi_to?: string; form?: string };

/** Validate an IMSI range before sending it.
 *
 * The backend enforces all of this (and the database enforces the overlap
 * rule); this exists so the user finds out while typing rather than after a
 * round trip. Comparison is done on strings of equal length, which is exactly
 * numeric ordering and never depends on Number.MAX_SAFE_INTEGER.
 */
export function validateSeries(
  from: string,
  to: string,
  existing: ImsiSeries[],
  editingId?: number
): SeriesErrors {
  const errors: SeriesErrors = {};

  if (!from) errors.imsi_from = 'Required.';
  else if (!/^\d+$/.test(from)) errors.imsi_from = 'Digits only.';
  else if (from.length > IMSI_WIDTH) errors.imsi_from = `At most ${IMSI_WIDTH} digits.`;

  if (!to) errors.imsi_to = 'Required.';
  else if (!/^\d+$/.test(to)) errors.imsi_to = 'Digits only.';
  else if (to.length > IMSI_WIDTH) errors.imsi_to = `At most ${IMSI_WIDTH} digits.`;

  if (errors.imsi_from || errors.imsi_to) return errors;

  // Pad the way the backend does: '0' for the lower bound, '9' for the upper,
  // so a prefix-shaped series covers its whole block.
  const low = from.padEnd(IMSI_WIDTH, '0');
  const high = to.padEnd(IMSI_WIDTH, '9');

  if (low > high) {
    errors.imsi_to = 'To must not be below From.';
    return errors;
  }

  const clash = existing.find((s) => {
    if (s.id === editingId || s.is_catch_all) return false;
    return s.imsi_from.padEnd(IMSI_WIDTH, '0') <= high && low <= s.imsi_to.padEnd(IMSI_WIDTH, '9');
  });
  if (clash) {
    errors.form = `Overlaps the existing series ${clash.imsi_from}–${clash.imsi_to}.`;
  }

  return errors;
}

/** How many subscribers a series covers -- the cheapest way to notice a range
 *  that accidentally spans ten million SIMs.
 *
 *  Plain Number is exact here: the widest possible range is 10^15, comfortably
 *  under Number.MAX_SAFE_INTEGER (~9.007 * 10^15). The bounds themselves stay
 *  strings everywhere else, where leading zeros matter. */
function rangeSize(series: ImsiSeries): string {
  const low = Number(series.imsi_from.padEnd(IMSI_WIDTH, '0'));
  const high = Number(series.imsi_to.padEnd(IMSI_WIDTH, '9'));
  if (!Number.isFinite(low) || !Number.isFinite(high) || high < low) return '-';
  return (high - low + 1).toLocaleString();
}

const Brands: React.FC = () => {
  // Hiding these is a courtesy; the API refuses the writes regardless.
  const { canEdit } = useAuth();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<Status>(null);

  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [seriesByBrand, setSeriesByBrand] = useState<Record<number, ImsiSeries[]>>({});
  const [loadingSeries, setLoadingSeries] = useState<Set<number>>(new Set());

  const [isBrandModalOpen, setIsBrandModalOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [brandForm, setBrandForm] = useState({ name: '', description: '', is_active: true, copy_global_allow_list: false });

  const [isSeriesModalOpen, setIsSeriesModalOpen] = useState(false);
  const [seriesBrandId, setSeriesBrandId] = useState<number | null>(null);
  const [editingSeries, setEditingSeries] = useState<ImsiSeries | null>(null);
  const [seriesForm, setSeriesForm] = useState({ imsi_from: '', imsi_to: '', label: '' });
  const [seriesErrors, setSeriesErrors] = useState<SeriesErrors>({});

  const [confirm, setConfirm] = useState<
    | { kind: 'delete-brand'; brand: Brand }
    | { kind: 'delete-series'; brandId: number; series: ImsiSeries }
    | null
  >(null);

  const fetchBrands = useCallback(async () => {
    try {
      setLoading(true);
      setBrands(await apiService.getBrands());
    } catch (error) {
      setStatus({ kind: 'error', text: `Could not load brands: ${(error as Error).message}` });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  const loadSeries = useCallback(async (brandId: number) => {
    try {
      setLoadingSeries((prev) => new Set(prev).add(brandId));
      const series = await apiService.getImsiSeries(brandId);
      setSeriesByBrand((prev) => ({ ...prev, [brandId]: series }));
    } catch (error) {
      setStatus({ kind: 'error', text: `Could not load IMSI series: ${(error as Error).message}` });
    } finally {
      setLoadingSeries((prev) => {
        const next = new Set(prev);
        next.delete(brandId);
        return next;
      });
    }
  }, []);

  const toggleRow = async (brandId: number) => {
    const next = new Set(expandedRows);
    if (next.has(brandId)) {
      next.delete(brandId);
    } else {
      next.add(brandId);
      if (!seriesByBrand[brandId]) await loadSeries(brandId);
    }
    setExpandedRows(next);
  };

  // --- brand form ---

  const openCreateBrand = () => {
    setEditingBrand(null);
    setBrandForm({ name: '', description: '', is_active: true, copy_global_allow_list: true });
    setIsBrandModalOpen(true);
  };

  const openEditBrand = (brand: Brand) => {
    setEditingBrand(brand);
    setBrandForm({
      name: brand.name,
      description: brand.description || '',
      is_active: brand.is_active,
      copy_global_allow_list: false,
    });
    setIsBrandModalOpen(true);
  };

  const saveBrand = async () => {
    try {
      setSaving(true);
      setStatus(null);
      if (editingBrand) {
        await apiService.updateBrand(editingBrand.id, {
          name: brandForm.name,
          description: brandForm.description || null,
          is_active: brandForm.is_active,
        });
        setStatus({ kind: 'ok', text: `Brand "${brandForm.name}" saved.` });
      } else {
        await apiService.createBrand({
          name: brandForm.name,
          description: brandForm.description || null,
          is_active: brandForm.is_active,
          copy_global_allow_list: brandForm.copy_global_allow_list,
        });
        setStatus({ kind: 'ok', text: `Brand "${brandForm.name}" created.` });
      }
      setIsBrandModalOpen(false);
      await fetchBrands();
    } catch (error) {
      setStatus({ kind: 'error', text: `Could not save the brand: ${(error as Error).message}` });
    } finally {
      setSaving(false);
    }
  };

  const deleteBrand = async (brand: Brand) => {
    try {
      setSaving(true);
      setStatus(null);
      await apiService.deleteBrand(brand.id);
      setConfirm(null);
      setStatus({ kind: 'ok', text: `Brand "${brand.name}" deleted.` });
      await fetchBrands();
    } catch (error) {
      setStatus({ kind: 'error', text: `Could not delete the brand: ${(error as Error).message}` });
    } finally {
      setSaving(false);
    }
  };

  // --- IMSI series form ---

  const openAddSeries = (brandId: number) => {
    setSeriesBrandId(brandId);
    setEditingSeries(null);
    setSeriesForm({ imsi_from: '', imsi_to: '', label: '' });
    setSeriesErrors({});
    setIsSeriesModalOpen(true);
  };

  const openEditSeries = (brandId: number, series: ImsiSeries) => {
    setSeriesBrandId(brandId);
    setEditingSeries(series);
    setSeriesForm({ imsi_from: series.imsi_from, imsi_to: series.imsi_to, label: series.label || '' });
    setSeriesErrors({});
    setIsSeriesModalOpen(true);
  };

  const onSeriesFieldChange = (field: 'imsi_from' | 'imsi_to' | 'label', value: string) => {
    const next = { ...seriesForm, [field]: value };
    setSeriesForm(next);
    if (field !== 'label' && seriesBrandId !== null) {
      setSeriesErrors(
        validateSeries(next.imsi_from, next.imsi_to, seriesByBrand[seriesBrandId] || [], editingSeries?.id)
      );
    }
  };

  const saveSeries = async () => {
    if (seriesBrandId === null) return;
    const errors = validateSeries(
      seriesForm.imsi_from, seriesForm.imsi_to, seriesByBrand[seriesBrandId] || [], editingSeries?.id
    );
    setSeriesErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      setSaving(true);
      setStatus(null);
      const payload = {
        imsi_from: seriesForm.imsi_from,
        imsi_to: seriesForm.imsi_to,
        label: seriesForm.label || undefined,
      };
      if (editingSeries) {
        await apiService.updateImsiSeries(seriesBrandId, editingSeries.id, payload);
      } else {
        await apiService.addImsiSeries(seriesBrandId, payload);
      }
      setIsSeriesModalOpen(false);
      setStatus({ kind: 'ok', text: 'IMSI series saved.' });
      await loadSeries(seriesBrandId);
      // Refresh the parent list so the series count on the row is current.
      await fetchBrands();
    } catch (error) {
      setStatus({ kind: 'error', text: `Could not save the IMSI series: ${(error as Error).message}` });
    } finally {
      setSaving(false);
    }
  };

  const deleteSeries = async (brandId: number, series: ImsiSeries) => {
    try {
      setSaving(true);
      setStatus(null);
      await apiService.deleteImsiSeries(brandId, series.id);
      setConfirm(null);
      setStatus({ kind: 'ok', text: 'IMSI series deleted.' });
      await loadSeries(brandId);
      await fetchBrands();
    } catch (error) {
      setStatus({ kind: 'error', text: `Could not delete the IMSI series: ${(error as Error).message}` });
    } finally {
      setSaving(false);
    }
  };

  const seriesFormValid =
    seriesForm.imsi_from !== '' && seriesForm.imsi_to !== '' && Object.keys(seriesErrors).length === 0;

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Brands</h1>
          <p className="mt-2 text-sm text-gray-700">
            Each brand owns one or more IMSI series and its own list of allowed visited networks.
            A subscriber whose IMSI matches no series is denied.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          {canEdit && <button
            type="button"
            onClick={openCreateBrand}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            <PlusIcon className="mr-2 h-4 w-4" />
            Add brand
          </button>}
        </div>
      </div>

      <StatusBanner status={status} />

      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Name</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Description</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">IMSI series</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Networks</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                    <th className="relative py-3.5 pl-3 pr-4 sm:pr-6"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {loading ? (
                    <tr><td colSpan={6} className="text-center py-4">Loading...</td></tr>
                  ) : brands.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-6 text-sm text-gray-500">
                        No brands yet.{canEdit && <>{' '}
                        <button onClick={openCreateBrand} className="text-indigo-600 hover:text-indigo-900 underline">
                          Add the first one
                        </button></>}
                      </td>
                    </tr>
                  ) : (
                    brands.map((brand) => (
                      <Fragment key={brand.id}>
                        <tr>
                          <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                            <button onClick={() => toggleRow(brand.id)} className="inline-flex items-center">
                              {expandedRows.has(brand.id)
                                ? <ChevronDownIcon className="mr-1 h-4 w-4 text-gray-400" />
                                : <ChevronRightIcon className="mr-1 h-4 w-4 text-gray-400" />}
                              {brand.name}
                            </button>
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-500">{brand.description || '-'}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <button
                              onClick={() => toggleRow(brand.id)}
                              className="text-blue-600 hover:text-blue-900 underline"
                            >
                              {brand.series_count}
                            </button>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <Link
                              to={`/brand-networks?brand=${brand.id}`}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              {brand.allowed_count} allowed
                            </Link>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <span
                              className={`inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded ${
                                brand.is_active ? 'text-green-700 bg-green-100' : 'text-gray-600 bg-gray-100'
                              }`}
                            >
                              {brand.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 space-x-3">
                            {canEdit && <>
                              <button onClick={() => openEditBrand(brand)} className="text-indigo-600 hover:text-indigo-900">
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => setConfirm({ kind: 'delete-brand', brand })}
                                className="text-red-600 hover:text-red-900"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </>}
                          </td>
                        </tr>

                        {expandedRows.has(brand.id) && (
                          <tr>
                            <td colSpan={6} className="px-6 py-4 bg-gray-50">
                              <div className="mb-4 flex items-center justify-between">
                                <h4 className="text-sm font-medium text-gray-900">IMSI series</h4>
                                {canEdit && <button
                                  onClick={() => openAddSeries(brand.id)}
                                  className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                  <PlusIcon className="mr-1 h-3 w-3" />
                                  Add IMSI series
                                </button>}
                              </div>
                              {loadingSeries.has(brand.id) ? (
                                <div className="text-center py-2 text-sm text-gray-500">Loading IMSI series...</div>
                              ) : (seriesByBrand[brand.id]?.length ?? 0) > 0 ? (
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead>
                                    <tr>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From</th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">To</th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscribers covered</th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Label</th>
                                      <th className="relative px-3 py-2"><span className="sr-only">Actions</span></th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200 bg-white">
                                    {seriesByBrand[brand.id].map((series) => (
                                      <tr key={series.id}>
                                        <td className="px-3 py-2 text-sm text-gray-900 font-mono">{series.imsi_from}</td>
                                        <td className="px-3 py-2 text-sm text-gray-900 font-mono">{series.imsi_to}</td>
                                        <td className="px-3 py-2 text-sm text-gray-500">{rangeSize(series)}</td>
                                        <td className="px-3 py-2 text-sm text-gray-500">
                                          {series.is_catch_all ? (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                              Catch-all
                                            </span>
                                          ) : (
                                            series.label || '-'
                                          )}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-right space-x-2">
                                          {canEdit && <>
                                          <button
                                            onClick={() => openEditSeries(brand.id, series)}
                                            className="text-indigo-600 hover:text-indigo-900"
                                          >
                                            <PencilIcon className="h-4 w-4" />
                                          </button>
                                          <button
                                            onClick={() => setConfirm({ kind: 'delete-series', brandId: brand.id, series })}
                                            className="text-red-600 hover:text-red-900"
                                          >
                                            <TrashIcon className="h-4 w-4" />
                                          </button>
                                          </>}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : (
                                <p className="text-sm text-gray-500 text-center py-2">
                                  No IMSI series configured — no subscriber resolves to this brand.
                                </p>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Brand form */}
      <Modal
        open={isBrandModalOpen}
        onClose={() => setIsBrandModalOpen(false)}
        title={editingBrand ? 'Edit brand' : 'Add brand'}
        footer={
          <>
            <button
              type="button"
              onClick={saveBrand}
              disabled={saving || !brandForm.name}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : editingBrand ? 'Save' : 'Create'}
            </button>
            <button
              type="button"
              onClick={() => setIsBrandModalOpen(false)}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
            >
              Cancel
            </button>
          </>
        }
      >
        <div>
          <label htmlFor="brand-name" className="block text-sm font-medium text-gray-700">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="brand-name"
            value={brandForm.name}
            onChange={(e) => setBrandForm({ ...brandForm, name: e.target.value })}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>
        <div>
          <label htmlFor="brand-description" className="block text-sm font-medium text-gray-700">Description</label>
          <textarea
            id="brand-description"
            rows={2}
            value={brandForm.description}
            onChange={(e) => setBrandForm({ ...brandForm, description: e.target.value })}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>
        <div className="flex items-center">
          <input
            id="brand-active"
            type="checkbox"
            checked={brandForm.is_active}
            onChange={(e) => setBrandForm({ ...brandForm, is_active: e.target.checked })}
            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          />
          <label htmlFor="brand-active" className="ml-2 block text-sm text-gray-900">Active</label>
        </div>
        {!brandForm.is_active && (
          <p className="text-xs text-yellow-700 bg-yellow-50 rounded p-2">
            An inactive brand is not a pause: every subscriber in its IMSI series will be denied.
          </p>
        )}
        {!editingBrand && (
          <div className="flex items-center">
            <input
              id="brand-copy-template"
              type="checkbox"
              checked={brandForm.copy_global_allow_list}
              onChange={(e) => setBrandForm({ ...brandForm, copy_global_allow_list: e.target.checked })}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="brand-copy-template" className="ml-2 block text-sm text-gray-900">
              Start from the global template allow list
            </label>
          </div>
        )}
      </Modal>

      {/* IMSI series form */}
      <Modal
        open={isSeriesModalOpen}
        onClose={() => setIsSeriesModalOpen(false)}
        title={editingSeries ? 'Edit IMSI series' : 'Add IMSI series'}
        footer={
          <>
            <button
              type="button"
              onClick={saveSeries}
              disabled={saving || !seriesFormValid}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : editingSeries ? 'Save' : 'Create'}
            </button>
            <button
              type="button"
              onClick={() => setIsSeriesModalOpen(false)}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
            >
              Cancel
            </button>
          </>
        }
      >
        <p className="text-xs text-gray-500">
          Enter the range as digits. A short value covers its whole block: 218901–218901 means every
          IMSI starting 218901.
        </p>
        <div>
          <label htmlFor="series-from" className="block text-sm font-medium text-gray-700">
            From <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="series-from"
            inputMode="numeric"
            value={seriesForm.imsi_from}
            onChange={(e) => onSeriesFieldChange('imsi_from', e.target.value)}
            className={`mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm font-mono ${
              seriesErrors.imsi_from ? 'border-red-300' : 'border-gray-300'
            }`}
          />
          {seriesErrors.imsi_from && <p className="mt-1 text-sm text-red-600">{seriesErrors.imsi_from}</p>}
        </div>
        <div>
          <label htmlFor="series-to" className="block text-sm font-medium text-gray-700">
            To <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="series-to"
            inputMode="numeric"
            value={seriesForm.imsi_to}
            onChange={(e) => onSeriesFieldChange('imsi_to', e.target.value)}
            className={`mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm font-mono ${
              seriesErrors.imsi_to ? 'border-red-300' : 'border-gray-300'
            }`}
          />
          {seriesErrors.imsi_to && <p className="mt-1 text-sm text-red-600">{seriesErrors.imsi_to}</p>}
        </div>
        <div>
          <label htmlFor="series-label" className="block text-sm font-medium text-gray-700">Label</label>
          <input
            type="text"
            id="series-label"
            value={seriesForm.label}
            onChange={(e) => onSeriesFieldChange('label', e.target.value)}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>
        {seriesErrors.form && <p className="text-sm text-red-600">{seriesErrors.form}</p>}
      </Modal>

      <ConfirmDialog
        open={confirm?.kind === 'delete-brand'}
        title="Delete brand"
        destructive
        busy={saving}
        confirmLabel="Delete"
        message={
          confirm?.kind === 'delete-brand' ? (
            <>
              Delete <strong>{confirm.brand.name}</strong>? This also removes its{' '}
              {confirm.brand.series_count} IMSI series and its allow list of {confirm.brand.rule_count}{' '}
              networks. Subscribers in those series will be denied unless another series claims them.
              This cannot be undone.
            </>
          ) : null
        }
        onConfirm={() => confirm?.kind === 'delete-brand' && deleteBrand(confirm.brand)}
        onCancel={() => setConfirm(null)}
      />

      <ConfirmDialog
        open={confirm?.kind === 'delete-series'}
        title="Delete IMSI series"
        destructive
        busy={saving}
        confirmLabel="Delete"
        message={
          confirm?.kind === 'delete-series' ? (
            <>
              Delete the series <strong>{confirm.series.imsi_from}–{confirm.series.imsi_to}</strong>?
              {confirm.series.is_catch_all
                ? ' This is the catch-all series: once it is gone, every IMSI outside a configured series is denied.'
                : ' Subscribers in this range will fall through to the catch-all series, or be denied if there is none.'}
            </>
          ) : null
        }
        onConfirm={() => confirm?.kind === 'delete-series' && deleteSeries(confirm.brandId, confirm.series)}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
};

export default Brands;
